import { Injectable } from '@angular/core';
import { Http, Response } from '@angular/http';
import { tokenNotExpired } from 'angular2-jwt';
import { Observable } from 'rxjs/Observable';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { environment as config } from '../../../environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { isEmpty, merge, is } from 'ramda/es';
import * as store from 'store';
import { map, first, catchError } from 'rxjs/operators';
import { ThemeDirective } from '../../theme/theme.directive';
export interface IAuthService {
  login: (credentials: any, accountType: string, provider: string) => Observable<any>;
}

@Injectable()
export class AuthService implements IAuthService {
  public static TOKEN_KEY = 'CURRENT_TOKEN';
  public static API_TOKEN_KEY = 'CURRENT_API_TOKEN';
  public static USER_KEY = 'USER_DATA';
  public static PROVIDER_KEY = 'CURRENT_PROVIDER';
  public static THEME = 'THEME';
  private isLogged = new BehaviorSubject<boolean>(this.isAuthenticated());
  public token: string;
  public user: any;
  private authConfig = {
    redirect_uri: config.auth.redirect,
    authEndpoint: `${config.auth.url}/auth/oauth`,
    linkedin: {
      redirect:
        'https://www.linkedin.com/oauth/v2/authorization?' +
        `redirect_uri=${config.auth.redirect}&response_type=code` +
        `&client_id=${config.linkedin.clientId}`
    },
    facebook: {
      redirect:
        'https://www.facebook.com/v2.8/dialog/oauth?' +
        `redirect_uri=${config.auth.redirect}&scope=email&` +
        `&client_id=${config.facebook.clientId}`
    },
    google: {
      redirect:
        'https://accounts.google.com/o/oauth2/v2/' +
        `auth?response_type=code` +
        `&redirect_uri=${config.auth.redirect}&scope=email%20profile` +
        `&client_id=${config.google.clientId}`
    }
  };

  constructor(
    private http: Http,
    private location: Location,
    private router: Router,
    private translateService: TranslateService
  ) {}

  public isAuthenticated(): boolean {
    return tokenNotExpired(AuthService.TOKEN_KEY)
      && tokenNotExpired(AuthService.API_TOKEN_KEY);
  }

  public isLoggedIn(): Observable<boolean> {
    return this.isLogged.asObservable();
  }

  public init(): void {
    const source$ = this.checkUser();
    const gotUser$ = map((res: any) => {
      if (res.type === 'oauth' || res.type === 'normal') {
        this.token = res.token;
        this.user = res.user;
        this.isLogged.next(!!res.user);
      }
    });
    source$.pipe(gotUser$).subscribe(null, err => {
      console.error(err);
      this.logout();
    });
  }

  public static retrieve(key: string) {
    return store.get(key);
  }

  public getUser() {
    return JSON.parse(AuthService.retrieve(AuthService.USER_KEY) || '{}');
  }

  private checkUser(): Observable<any> {
    return Observable.create(res => {
      const token = AuthService.retrieve(AuthService.TOKEN_KEY);
      const user = JSON.parse(AuthService.retrieve(AuthService.USER_KEY) || '{}');
      const provider = AuthService.retrieve(AuthService.PROVIDER_KEY);
      if (provider && provider !== 'local' && is(Object, user) && !isEmpty(user) && token) {
        return res.next({ token, user, type: 'oauth' });
      }
      if (token && is(Object, user)) {
        return res.next({ token, user, type: 'normal' });
      }
      if (provider && !user) {
        const code = new URLSearchParams(this.location.path(false).split('?')[1]).get('code');
        if (config && code) {
          const payload = { provider, code, clientId: config[provider].clientId };
          return this.oauthLogin(payload).subscribe(
            user => res.next({ token, user, type: 'check' }),
            err => res.error(err)
          );
        }
      }
      const error = this.translateService.instant('auth.errors.server');
      return res.error(new Error(error));
    });
  }

  login(credentials, accountType): Observable<any> {
    return this.http
      .post(`${config.auth.url}/auth/${accountType}/login`, credentials)
      .pipe(
        map((response: Response) => {
          const token = response.headers.get('authorization');
          if (token) {
            this.token = token;
            this.user = { name: credentials.login };
            store.set(AuthService.TOKEN_KEY, token);
            store.set(AuthService.USER_KEY, JSON.stringify(this.user));
            store.remove(ThemeDirective.THEME);
            this.isLogged.next(!!this.user);
            const api_token = response.headers.get('x-api-token');
            if (api_token) {
              store.set(AuthService.API_TOKEN_KEY, api_token);
            }
            return response;
          }
          return this.handleError(false);
        })
      )
      .catch(err => this.handleError(err));
  }

  private handleError(err) {
    let error =
      err && typeof err._body === 'string'
        ? JSON.parse(err._body).message
        : this.translateService.instant('auth.errors.server');

    if (err.status && err.status === 401) {
      error = this.translateService.instant('auth.errors.credentials');
    }
    return Observable.throw(error);
  }

  logout(): void {
    delete this.user;
    delete this.token;
    store.remove(AuthService.TOKEN_KEY);
    store.remove(AuthService.API_TOKEN_KEY);
    store.remove(AuthService.USER_KEY);
    store.remove(AuthService.PROVIDER_KEY);
    store.remove(ThemeDirective.THEME);
    this.isLogged.next(false);
    this.router.navigate(['/login']);
  }

  checkOauth(token: string): Observable<any> {
    const provider = store.get(AuthService.PROVIDER_KEY);
    return this.http
      .get(`${config.auth.url}/auth/check/${provider}/oauth/${token}`)
      .pipe(
        map((res: Response) => res.json),
        map((res: any) => {
          const token = res.token;
          const user = res.user;
          if (is(Object, user) && !isEmpty(user)) {
            this.token = token;
            this.user = user;
            store.set(AuthService.TOKEN_KEY, token);
            store.set(AuthService.USER_KEY, JSON.stringify(user));
          }
          return user;
        })
      )
      .catch(err => this.handleError(err));
  }

  oauthLogin(payload: any): Observable<any> {
    const body = merge(payload, { redirect: this.authConfig.redirect_uri });
    return this.http
      .post(this.authConfig.authEndpoint, body)
      .pipe(
        map((res: Response) => res.json()),
        map(res => {
          store.set(AuthService.TOKEN_KEY, res.token);
          store.set(AuthService.USER_KEY, JSON.stringify(res.user));
          return res.user;
        })
      )
      .catch(err => this.handleError(err));
  }

  redirectToProvider(provider): void {
    store.set(AuthService.PROVIDER_KEY, provider);
    window.location.href = this.authConfig[provider].redirect;
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/auth/auth.service.ts